import { RECAPTCHA_URL_MAP } from '@/constants/config';
import { RECAPTCHA_LANG_MAP } from '@/constants/locale';

const state = {
  loaded: false,
  timeoutId: 0,
  loadingPromise: null as Promise<void> | null,
};

const resetTimeoutId = () => {
  if (state.timeoutId) {
    clearTimeout(state.timeoutId);
    state.timeoutId = 0;
  }
};

export const getRecaptchaLang = (langCode: string): string => {
  return RECAPTCHA_LANG_MAP[langCode] || 'en';
};

export const getScriptUrl = (googleVersion: string, gsk3: string, location: string, lang: string): string => {
  const params = googleVersion === '2' ? { render: 'explicit', hl: lang } : { render: gsk3, hl: lang };
  const baseUrl = location !== 'CN' ? RECAPTCHA_URL_MAP.US : RECAPTCHA_URL_MAP.CN;
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  url.searchParams.set('_t', Date.now().toString());
  return url.href;
};

const getEnterprise = () => window.grecaptcha?.enterprise;

const runWhenReady = <T>(
  action: (enterprise: NonNullable<ReturnType<typeof getEnterprise>>) => T | Promise<T>,
): Promise<T> => {
  return new Promise((resolve, reject) => {
    const enterprise = getEnterprise();
    if (!enterprise) {
      reject(new Error('reCAPTCHA enterprise not available'));
      return;
    }

    enterprise.ready?.(() => {
      Promise.resolve(action(enterprise))
        .then(resolve)
        .catch((error) => {
          reject(error instanceof Error ? error : new Error('reCAPTCHA operation failed'));
        });
    });
  });
};

export const loadScript = (scriptUrl: string): Promise<void> => {
  if (state.loaded) return Promise.resolve();
  if (state.loadingPromise) return state.loadingPromise;

  state.loadingPromise = new Promise((resolve, reject) => {
    resetTimeoutId();

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.id = 'recaptcha-script';
    script.async = true;
    script.defer = true;

    const failLoad = (message: string) => {
      resetTimeoutId();
      state.loadingPromise = null;
      reject(new Error(message));
    };

    script.onerror = () => {
      failLoad('Failed to load reCAPTCHA script');
    };

    script.onload = () => {
      resetTimeoutId();
      state.loaded = true;
      state.loadingPromise = null;
      resolve();
    };

    state.timeoutId = window.setTimeout(() => {
      failLoad('reCAPTCHA script load timed out');
    }, 10 * 1000);

    document.head.appendChild(script);
  });

  return state.loadingPromise;
};

export const cleanScript = (): void => {
  delete window.grecaptcha;
  delete window.___grecaptcha_cfg;

  const scriptElement = document.getElementById('recaptcha-script');
  if (scriptElement) {
    document.head.removeChild(scriptElement);
  }

  state.loaded = false;
  state.loadingPromise = null;
  resetTimeoutId();
};

export const executeV3 = (siteKey: string, action: string): Promise<string> => {
  return runWhenReady((enterprise) => {
    const execute = enterprise.execute?.(siteKey, { action });
    if (!execute) {
      throw new Error('reCAPTCHA enterprise not available');
    }

    return execute.catch(() => {
      throw new Error('reCAPTCHA v3 execution failed');
    });
  });
};

export const renderV2 = (
  container: string | HTMLDivElement,
  options: {
    sitekey: string;
    action: string;
    theme: string;
    hl: string;
    callback: (token: string) => void;
    'expired-callback': () => void;
    'error-callback': () => void;
  },
): Promise<void> => {
  return runWhenReady((enterprise) => {
    try {
      enterprise.render?.(container, options) as object;
    } catch (error) {
      throw error instanceof Error ? error : new Error('reCAPTCHA v2 render failed');
    }
  });
};

export const resetV2 = (): void => {
  const enterprise = getEnterprise();
  enterprise?.reset?.();
};
